import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import './theme.css';

/**
 * PRIMITIVAS
 *
 * Sem regra de negócio e sem cor literal — toda cor sai de um token do
 * `@theme`, que o painel sobrescreve em tempo de execução.
 */

/** Junta classes ignorando falsos. Evita `undefined` no atributo. */
export function cx(...values: (string | false | null | undefined)[]): string {
  return values.filter(Boolean).join(' ');
}

// ------------------------------------------------------------------ botão

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 rounded-(--radius-control) font-semibold ' +
  'transition-colors duration-150 disabled:opacity-55 disabled:cursor-not-allowed ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';

const BUTTON_VARIANT = {
  primary: 'bg-primary text-primary-fg hover:bg-primary-hover',
  accent: 'bg-accent text-accent-fg hover:brightness-95',
  outline: 'border border-line-strong bg-surface text-ink hover:bg-surface-muted',
  ghost: 'text-ink hover:bg-surface-muted',
  danger: 'bg-danger text-white hover:brightness-110',
} as const;

const BUTTON_SIZE = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-5 text-[0.9375rem]',
  lg: 'h-13 px-7 text-base',
} as const;

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof BUTTON_VARIANT;
  size?: keyof typeof BUTTON_SIZE;
  block?: boolean;
  loading?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', block, loading, disabled, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cx(BUTTON_BASE, BUTTON_VARIANT[variant], BUTTON_SIZE[size], block && 'w-full', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? 'Aguarde…' : children}
    </button>
  );
});

/** Mesma aparência do botão, semântica de link. */
export const ButtonLink = forwardRef<HTMLAnchorElement, AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: keyof typeof BUTTON_VARIANT;
  size?: keyof typeof BUTTON_SIZE;
  block?: boolean;
}>(function ButtonLink({ variant = 'primary', size = 'md', block, className, children, ...rest }, ref) {
  return (
    <a
      ref={ref}
      className={cx(BUTTON_BASE, BUTTON_VARIANT[variant], BUTTON_SIZE[size], block && 'w-full', 'no-underline', className)}
      {...rest}
    >
      {children}
    </a>
  );
});

// ---------------------------------------------------------------- campos

const FIELD_BASE =
  'w-full rounded-(--radius-control) border border-line bg-surface px-3 text-ink ' +
  'placeholder:text-ink-faint transition-colors ' +
  'focus:border-primary focus:outline-none ' +
  'aria-invalid:border-danger aria-invalid:bg-danger-soft ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cx(FIELD_BASE, 'h-11', className)} {...rest} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={cx(FIELD_BASE, 'min-h-28 py-2.5 resize-y', className)} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={cx(FIELD_BASE, 'h-11 appearance-none pr-8 cursor-pointer', className)} {...rest}>
        {children}
      </select>
    );
  },
);

/**
 * Envelope de campo. Concentra a acessibilidade — label associado, ajuda e
 * erro anunciados — para que nenhum controle do registry reimplemente isso.
 */
export function FieldShell({
  inputId,
  label,
  help,
  error,
  required,
  children,
}: {
  inputId: string;
  label: ReactNode;
  help?: string | null;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-semibold text-ink" htmlFor={inputId}>
        {label}
        {required ? (
          <span className="ml-0.5 text-danger" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      {children}

      {help ? (
        <span className="text-xs text-ink-faint" id={`${inputId}-help`}>
          {help}
        </span>
      ) : null}

      {error ? (
        <span className="text-xs font-medium text-danger" id={`${inputId}-error`} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function describedBy(inputId: string, help?: string | null, error?: string): string | undefined {
  const ids = [help ? `${inputId}-help` : null, error ? `${inputId}-error` : null].filter(Boolean);
  return ids.length ? ids.join(' ') : undefined;
}

// ------------------------------------------------------------ superfícies

const BADGE_TONE = {
  neutral: 'bg-surface-muted text-ink-soft',
  primary: 'bg-primary-soft text-primary',
  accent: 'bg-accent-soft text-accent-fg',
  danger: 'bg-danger-soft text-danger',
  solid: 'bg-primary text-primary-fg',
} as const;

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: keyof typeof BADGE_TONE;
  className?: string;
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.6875rem] font-semibold tracking-wide uppercase',
        BADGE_TONE[tone] ?? BADGE_TONE.neutral,
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('rounded-(--radius-card) border border-line bg-surface shadow-(--shadow-card)', className)}>
      {children}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('animate-pulse rounded-md bg-surface-muted', className)} aria-hidden="true" />;
}

export function Alert({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'positive' | 'danger' | 'warning';
}) {
  const tones = {
    neutral: 'border-line bg-surface-muted text-ink',
    // Confirmacao de acao. Faltava, e o painel vinha anunciando sucesso com a
    // mesma caixa cinza do aviso — o que obriga a ler para saber se deu certo.
    positive: 'border-positive/40 bg-positive-soft text-positive',
    danger: 'border-danger/40 bg-danger-soft text-danger',
    warning: 'border-warning/40 bg-warning-soft text-warning',
  } as const;

  return (
    <div
      className={cx('rounded-(--radius-control) border px-4 py-3 text-[0.9375rem]', tones[tone])}
      role={tone === 'neutral' ? undefined : 'alert'}
    >
      {children}
    </div>
  );
}

export function Container({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('page-shell', className)}>{children}</div>;
}

/**
 * Cabeçalho de seção da home. Título à esquerda, ação opcional à direita —
 * o par que se repete em toda a página inicial.
 */
export function SectionHeading({
  eyebrow,
  title,
  subtitle,
  action,
}: {
  eyebrow?: string | null;
  title: string;
  subtitle?: string | null;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="flex flex-col gap-1.5 max-w-2xl">
        {eyebrow ? (
          <span className="text-xs font-bold uppercase tracking-[0.14em] text-primary">{eyebrow}</span>
        ) : null}
        <h2 className="text-2xl font-bold text-ink md:text-3xl">{title}</h2>
        {subtitle ? <p className="text-ink-soft text-pretty">{subtitle}</p> : null}
      </div>
      {action}
    </div>
  );
}

// ------------------------------------------------------------- listas

export { DataTable, Pagination, usePagination, type Column } from './DataTable';
export { Lookup, type LookupOption } from './Lookup';
